import React, { useContext, useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { SpinnerDarkIcon } from 'app/components/shared/icons';
import EditPencilIcon from 'app/components/shared/icons/edit-pencil';
import useOnClickOutside from 'hooks/click-outside';
import { postRoleDescription } from 'services/profile-editor';

import PeopleDrawerContactIcon from '../PeopleDrawerContactIcon';
import { PeopleDrawerContext } from '../PeopleDrawerContext';
import {
  PeopleDrawerRoleDescription,
  PeopleDrawerRoleLoadingContainer,
  PeopleDrawerRoleTitle,
  PeopleDrawerRoleTitleContainer,
  PeopleDrawerRoleTitleWrapper,
  PeopleDrawerRoleWrapper,
} from './styled';

export default function PeopleDrawerRole() {
  const { person, isCurrentUser } = useContext(PeopleDrawerContext);
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [description, setDescription] = useState(person?.info?.role_description ?? '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isSavingRef = useRef(false);

  useEffect(() => {
    setDescription(person?.info?.role_description ?? '');
  }, [person?.info?.role_description]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(
        textareaRef.current.value.length,
        textareaRef.current.value.length
      );
    }
  }, [isEditing]);

  const handlePostRoleDescription = async (roleDescription: string) => {
    const response = await postRoleDescription({ value: roleDescription });
    if (!response.ok) {
      throw new Error(response.originalError?.message);
    }
    return response.data;
  };

  const { mutate, isLoading } = useMutation(handlePostRoleDescription, {
    onSuccess: () => {
      isSavingRef.current = false;
      setIsEditing(false);
      // Invalidate the person drawer query to refresh the data
      if (person?.info?.uuid) {
        queryClient.invalidateQueries(['collective-index-person-drawer', person.info.uuid]);
      }
    },
    onError: () => {
      isSavingRef.current = false;
      setIsEditing(false);
    },
  });

  const handleSave = () => {
    // Prevent multiple calls
    if (isLoading || isSavingRef.current) {
      return;
    }

    if (description !== (person?.info?.role_description ?? '')) {
      isSavingRef.current = true;
      mutate(description);
    } else {
      setIsEditing(false);
    }
  };

  useOnClickOutside(containerRef, () => {
    if (isEditing && !isLoading && !isSavingRef.current) {
      handleSave();
    }
  });

  const handleStartEditing = () => {
    if (isCurrentUser && !isEditing) {
      setIsEditing(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      if (!isLoading && !isSavingRef.current) {
        handleSave();
      }
    }
    if (e.key === 'Escape') {
      if (!isLoading && !isSavingRef.current) {
        setDescription(person?.info?.role_description ?? '');
        setIsEditing(false);
      }
    }
  };

  const renderDescriptionContent = () => {
    if (isEditing && isCurrentUser) {
      if (isLoading) {
        return (
          <PeopleDrawerRoleLoadingContainer>
            <SpinnerDarkIcon width={24} height={24} fill="var(--color-primary)" />
          </PeopleDrawerRoleLoadingContainer>
        );
      }

      return (
        <textarea
          ref={textareaRef}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          style={{
            fontFamily: 'inherit',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: 'normal',
            color: 'var(--color-text)',
            background: 'transparent',
            border: '1px solid var(--color-primary)',
            borderRadius: '4px',
            padding: '8px',
            width: '100%',
            minHeight: '60px',
            resize: 'vertical',
            outline: 'none',
          }}
        />
      );
    }

    return (
      <PeopleDrawerRoleDescription
        onClick={handleStartEditing}
        style={isCurrentUser ? { cursor: 'pointer' } : {}}
      >
        {description}
        {isCurrentUser ? (
          <button
            onClick={handleStartEditing}
            type="button"
            style={{
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              display: 'inline',
              alignItems: 'center',
              transform: 'translateX(4px)',
            }}
          >
            <EditPencilIcon width={13} height={14} fill="var(--color-primary)" />
          </button>
        ) : null}
      </PeopleDrawerRoleDescription>
    );
  };

  return (
    <PeopleDrawerRoleWrapper ref={containerRef}>
      <PeopleDrawerRoleTitleWrapper>
        <PeopleDrawerRoleTitleContainer>
          <PeopleDrawerRoleTitle>
            {person?.info?.name}’s role at {person?.info?.company_name}:
          </PeopleDrawerRoleTitle>
        </PeopleDrawerRoleTitleContainer>
        {isCurrentUser ? (
          <button
            onClick={handleStartEditing}
            type="button"
            style={{
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <EditPencilIcon width={20} height={20} fill="var(--color-primary)" />
          </button>
        ) : (
          <PeopleDrawerContactIcon />
        )}
      </PeopleDrawerRoleTitleWrapper>
      {renderDescriptionContent()}
    </PeopleDrawerRoleWrapper>
  );
}
